import { useUserStore } from '@/common/stores/userStore';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getDefaultRouteForUser } from '../guards/authGuard';
import {
  canAccessRoute,
  getHomeRouteForRole,
  getRoutesByCategory,
  getRoutesForRole,
} from '../routes';
import { AppRouteMeta, UserRole, RouteCategoryValue, ROUTE_CATEGORY } from '../types';

/**
 * Composable pour utiliser le système de routing avec gestion des rôles
 */
export function useAppRouter() {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();

  // Computed properties
  const userRoles = computed(() => userStore.userRoles);
  const isAuthenticated = computed(() => userStore.isAuthenticated);

  const currentUserRole = computed(() => {
    if (userRoles.value.includes(UserRole.ADMINISTRATOR)) {
      return UserRole.ADMINISTRATOR;
    } else if (userRoles.value.includes(UserRole.STUDENT)) {
      return UserRole.STUDENT;
    } else if (userRoles.value.includes(UserRole.COLLABORATEUR)) {
      return UserRole.COLLABORATEUR;
    }
    return null;
  });

  const homeRoute = computed(() => {
    if (currentUserRole.value) {
      return getHomeRouteForRole(currentUserRole.value);
    }
    return '/';
  });

  const accessibleRoutes = computed(() => {
    if (currentUserRole.value) {
      return getRoutesForRole(currentUserRole.value);
    }
    return [];
  });

  // Methods
  const navigateToHome = () => {
    router.push(homeRoute.value);
  };

  const navigateWithRoleCheck = (routeName: string, fallbackRoute = '/not-allowed') => {
    const targetRoute = accessibleRoutes.value.find((route) => route.name === routeName);
    if (targetRoute && canAccessRoute(userRoles.value, targetRoute)) {
      router.push({ name: routeName });
    } else {
      router.push(fallbackRoute);
    }
  };

  const getMenuRoutes = (category: RouteCategoryValue) => {
    return getRoutesByCategory(accessibleRoutes.value, category);
  };

  const getMenuRoutesByRoles = () => {
    let category: RouteCategoryValue;
    switch (currentUserRole.value) {
      case UserRole.STUDENT:
        category = ROUTE_CATEGORY.STUDENT;
        break;
      case UserRole.ADMINISTRATOR:
      default:
        category = ROUTE_CATEGORY.COLLABORATEUR;
        break;
    }
    return getRoutesByCategory(accessibleRoutes.value, category);
  };

  const getCommonRoutes = () => {
    return getRoutesByCategory(accessibleRoutes.value, ROUTE_CATEGORY.COMMON);
  };

  const canAccessCurrentRoute = computed(() => {
    // Créer un objet route compatible avec RouteRecordRaw
    const routeRecord = accessibleRoutes.value.find((r) => r.name === route.name);
    return routeRecord ? canAccessRoute(userRoles.value, routeRecord) : false;
  });

  const getCurrentRouteMeta = computed(() => {
    return route.meta as AppRouteMeta;
  });

  const redirectToAppropriateHome = () => {
    const defaultRoute = getDefaultRouteForUser(userRoles.value);
    router.push(defaultRoute);
  };

  const goBackOrHome = () => {
    if (window.history.length > 1) {
      router.back();
    } else {
      navigateToHome();
    }
  };

  return {
    // Computed
    userRoles,
    isAuthenticated,
    currentUserRole,
    homeRoute,
    accessibleRoutes,
    canAccessCurrentRoute,
    currentRouteMeta: getCurrentRouteMeta,

    // Methods
    navigateToHome,
    navigateWithRoleCheck,
    getMenuRoutes,
    getMenuRoutesByRoles,
    redirectToAppropriateHome,
    goBackOrHome,
    getCommonRoutes,

    // Vue Router methods
    router,
    route,
  };
}
